import { RRule } from 'rrule';
import { DateTime } from 'luxon';
import { formatDateLong, isValidPositiveInteger } from '@utriper/core';

export const FREQUENCY = {
    DAILY: 1,
    WEEKLY: 2,
    // MONTHLY: 3,
    WEEKENDS: 4,
    BUSINESS_DAYS: 5,
    CUSTOM: 6,
};

export const UNTIL_TYPE = {
    INFINITE: '1',
    DATE: '2',
    COUNT: '3',
};

const WEEK_DAYS = {
    MONDAY: 'MO',
    TUESDAY: 'TU',
    WEDNESDAY: 'WE',
    THURSDAY: 'TH',
    FRIDAY: 'FR',
    SATURDAY: 'SA',
    SUNDAY: 'SU',
};

const weekDaysByID = {
    0: WEEK_DAYS.MONDAY,
    1: WEEK_DAYS.TUESDAY,
    2: WEEK_DAYS.WEDNESDAY,
    3: WEEK_DAYS.THURSDAY,
    4: WEEK_DAYS.FRIDAY,
    5: WEEK_DAYS.SATURDAY,
    6: WEEK_DAYS.SUNDAY,
};

const weekDays = Object.values(WEEK_DAYS);
const weekendsDays = weekDays.slice(5, 7);
const bussinessDays = weekDays.slice(0, 5);

// EXTRACT RRULE UTILS
export const rruleExtractor = (rruleString, startUTC, timezone) => {
    const values = {
        repeat: false,
        selectedWeekDays: [],
        frequency: FREQUENCY.CUSTOM,
        intradayRepeat: false,
        byHour: null,
        byMinute: null,
        untilType: UNTIL_TYPE.INFINITE,
        untilCount: null,
        untilDate: null,
    };
    if (!rruleString) {
        return values;
    }

    const rrule = RRule.fromString(rruleString);
    if (!rrule?.options) {
        return values;
    }

    const { byweekday, byhour, byminute, count, until } = rrule.options;

    values.selectedWeekDays = getSelectedWeekDays(byweekday, startUTC, timezone);
    values.frequency = getFrequencyOption(byweekday, startUTC, timezone);

    if (values.selectedWeekDays?.length > 0 && values.frequency) {
        values.repeat = true;
    }

    const hasByHour = rrule?.origOptions?.byhour !== undefined;
    const hasByMinute = rrule?.origOptions?.byminute !== undefined;
    const intradayValues = getIntradayTimeValues(hasByHour ? byhour : [], hasByMinute ? byminute : []);

    if (isValidPositiveInteger(intradayValues.byHour) || isValidPositiveInteger(intradayValues.byMinute)) {
        values.intradayRepeat = true;
        values.byHour = intradayValues.byHour;
        values.byMinute = intradayValues.byMinute;
    }

    const untilValues = getUntilValues(count, until, timezone);

    values.untilType = untilValues.untilType;
    values.untilCount = untilValues.untilCount;
    values.untilDate = untilValues.untilDate;

    return values;
};

const getSelectedWeekDays = (byWeekDay = [], startUTC, timezone) => {
    if (!byWeekDay || byWeekDay?.length <= 0 || !startUTC) return [];

    const selectedWeekDays = byWeekDay.map((day) => weekDaysByID[Number(day).toString()]);
    const dateUTC = DateTime.fromISO(startUTC, { zone: 'UTC' });

    if (!dateUTC.isValid) {
        return [];
    }

    const adjustedDate = dateUTC.setZone(timezone, { keepLocalTime: false });

    const offsetHours = dateUTC.hour - adjustedDate.hour + adjustedDate.offset / 60;

    return selectedWeekDays.map((day) => {
        const dayIndex = weekDays.indexOf(day);

        let adjustedIndex = dayIndex;

        if (offsetHours < 0) {
            adjustedIndex = (dayIndex - 1) % 7 >= 0 ? (dayIndex - 1) % 7 : 6;
        }
        if (offsetHours > 0) {
            adjustedIndex = (dayIndex + 1) % 7 <= 6 ? (dayIndex + 1) % 7 : 0;
        }

        return weekDays[adjustedIndex];
    });
};

const getFrequencyOption = (selectedWeekDays = [], startUTC, timezone) => {
    if (selectedWeekDays?.length <= 0 || !startUTC) {
        return null;
    }

    const dateUTC = DateTime.fromISO(startUTC, { zone: 'UTC' });

    if (!dateUTC.isValid) {
        return null;
    }

    const adjustedDate = dateUTC.setZone(timezone, { keepLocalTime: false });
    const dateDay = weekDays[adjustedDate.weekday - 1];

    if (selectedWeekDays.length === 7) {
        return FREQUENCY.DAILY;
    }

    if (selectedWeekDays.length === weekendsDays.length && selectedWeekDays.every((day) => weekendsDays.includes(day))) {
        return FREQUENCY.WEEKENDS;
    }

    if (selectedWeekDays.length === bussinessDays.length && selectedWeekDays.every((day) => bussinessDays.includes(day))) {
        return FREQUENCY.BUSINESS_DAYS;
    }

    if (selectedWeekDays.length === 1 && selectedWeekDays.includes(dateDay)) {
        return FREQUENCY.WEEKLY;
    }

    return FREQUENCY.CUSTOM;
};

const getIntradayTimeValues = (hours = [], minutes = []) => {
    let byHour = null;
    let byMinute = null;

    if (Array.isArray(hours) && hours?.length > 0) {
        if (hours?.length > 1) {
            const differences = hours.slice(1).map((hour, idx) => hour - hours[idx]);
            byHour = differences.every((d) => d === differences?.[0]) ? differences[0] : null;
        } else {
            byHour = hours[0];
        }
    }

    if (Array.isArray(minutes) && minutes?.length > 1) {
        const differences = minutes.slice(1).map((minute, idx) => minute - minutes[idx]);
        const difference = differences.every((d) => d === differences?.[0]) ? differences[0] : null;
        if (difference > 59) {
            byHour = Math.floor(difference / 60);
            byMinute = difference % 60;
        } else {
            byMinute = difference;
        }
    }

    return { byHour, byMinute };
};

const getUntilValues = (count = 0, until = null, timezone) => {
    const untilValues = {
        untilType: UNTIL_TYPE.INFINITE,
        untilCount: count ?? null,
        untilDate: until ?? null,
    };

    if (count && count > 0) {
        untilValues.untilType = UNTIL_TYPE.COUNT;
    } else if (until) {
        untilValues.untilType = UNTIL_TYPE.DATE;
        const untilDate = DateTime.fromJSDate(until, { zone: 'utc' }).setZone(timezone, { keepLocalTime: false });

        untilValues.untilDate = untilDate;
    }

    return untilValues;
};

// BUILD RRULE UTILS
export const buildRRule = (values, translateOptions = { translate: null, language: 'es' }) => {
    const { start, startDuration, timezone, repeat, selectedWeekDays = [], frequency, intradayRepeat, byHour, byMinute, untilType, untilCount, untilDate } = values;

    if (!repeat || !start || !timezone || !frequency) {
        return null;
    }

    const startUTC = buildUTCDate(start, startDuration, timezone);

    if (!startUTC) {
        return null;
    }

    const rruleOptions = {
        freq: RRule.WEEKLY,
    };

    if (startUTC) {
        rruleOptions.dtstart = new Date(startUTC);
    }

    let adjustedWeekDays = [];
    if (frequency !== FREQUENCY.CUSTOM) {
        adjustedWeekDays = buildSelectedWeekDaysByFrequency(frequency, startUTC, timezone);
    } else {
        adjustedWeekDays = [...selectedWeekDays];
    }

    if (adjustedWeekDays?.length > 0) {
        adjustedWeekDays = buildSelectedWeekDays(adjustedWeekDays, startUTC, timezone);
        rruleOptions.byweekday = getByWeekDay(adjustedWeekDays);
    } else {
        return null;
    }

    if (intradayRepeat) {
        const { hours, minutes } = getIntradayForRRule(startUTC, timezone, byHour, byMinute);

        if (hours?.length > 0) {
            rruleOptions.byhour = hours;
        }
        if (minutes?.length > 0) {
            rruleOptions.byminute = minutes;
        }
    }

    if (untilType === UNTIL_TYPE.COUNT && untilCount > 0) {
        rruleOptions.count = untilCount;
    }

    if (untilType === UNTIL_TYPE.DATE && untilDate?.isValid) {
        rruleOptions.until = untilDate.toJSDate();
    }

    const rrule = new RRule(rruleOptions);
    const rruleString = rrule.toString();
    let rruleDescription = '';
    if (typeof translateOptions?.translate === 'function') {
        rruleDescription = getRRuleDescription(values, translateOptions);
    }

    return { rrule, rruleString: rruleString.substring(rruleString.indexOf('RRULE:')), rruleDescription };
};

export const buildUTCDate = (date, durationDate, zone) => {
    if (!date?.isValid || !durationDate?.isValid || !zone) {
        return null;
    }

    const utcDate = DateTime.fromObject(
        {
            year: date.year,
            month: date.month,
            day: date.day,
            hour: durationDate.hour,
            minute: durationDate.minute,
            second: 0,
            millisecond: 0,
        },
        { zone }
    );

    return utcDate.toUTC().toISO();
};

export const getDateValuesFromUTC = (startUTC, endUTC, zone) => {
    if (!startUTC || !endUTC || !zone) {
        return {
            start: null,
            startDuration: null,
            end: null,
            endDuration: null,
        };
    }

    const start = DateTime.fromISO(startUTC, { zone: 'utc' }).setZone(zone);
    const end = DateTime.fromISO(endUTC, { zone: 'utc' }).setZone(zone);

    return {
        start,
        startDuration: start,
        end,
        endDuration: end,
    };
};

const buildSelectedWeekDays = (selectedWeekDays = [], startUTC, timezone) => {
    if (selectedWeekDays?.length <= 0 || !startUTC) {
        return [];
    }

    const dateUTC = DateTime.fromISO(startUTC, { zone: 'UTC' });

    if (!dateUTC.isValid) {
        return [];
    }

    const adjustedDate = dateUTC.setZone(timezone, { keepLocalTime: false });

    const offsetHours = dateUTC.hour - adjustedDate.hour + adjustedDate.offset / 60;

    return selectedWeekDays.map((day) => {
        const dayIndex = weekDays.indexOf(day);
        let adjustedIndex = dayIndex;

        if (offsetHours < 0) {
            adjustedIndex = (dayIndex + 1) % 7;
        }
        if (offsetHours > 0) {
            adjustedIndex = (dayIndex - 1) % 7;
        }

        return weekDays[adjustedIndex];
    });
};

const buildSelectedWeekDaysByFrequency = (frequency, startUTC, timezone) => {
    if (frequency === FREQUENCY.CUSTOM) {
        return [];
    }

    if (frequency === FREQUENCY.DAILY) {
        return [...weekDays];
    }

    if (frequency === FREQUENCY.WEEKLY) {
        const dateUTC = DateTime.fromISO(startUTC, { zone: 'UTC' });

        if (!dateUTC.isValid) {
            return [];
        }

        const adjustedDate = dateUTC.setZone(timezone, { keepLocalTime: false });
        const dateDay = weekDays[adjustedDate.weekday - 1];

        return [dateDay];
    }

    if (frequency === FREQUENCY.WEEKENDS) {
        return [...weekendsDays];
    }

    if (frequency === FREQUENCY.BUSINESS_DAYS) {
        return [...bussinessDays];
    }

    return [];
};

const getByWeekDay = (selectedWeekDays = []) => {
    return selectedWeekDays.map((day) => RRule?.[day] ?? null).filter(Boolean);
};

const getIntradayForRRule = (startUTC, timezone, byHour, byMinute) => {
    const dateUTC = DateTime.fromISO(startUTC, { zone: 'UTC' });

    if (!dateUTC.isValid) {
        return [];
    }

    const adjustedDate = dateUTC.setZone(timezone, { keepLocalTime: false });

    const initialHour = dateUTC.hour;
    const initialMinutes = initialHour * 60 + dateUTC.minute;
    const maxMinutes = 1440 - adjustedDate.offset;
    const maxHours = maxMinutes / 60;
    let hours = [];
    let minutes = [];
    if (byHour && !byMinute) {
        for (let hour = initialHour; hour < maxHours; hour += byHour) {
            hours.push(hour);
        }
    } else if (!byHour && byMinute) {
        for (let minute = initialMinutes; minute < maxMinutes; minute += byMinute) {
            minutes.push(minute);
        }
        hours.push(0);
    } else if (byHour && byMinute) {
        const byMinuteWithHours = byHour * 60 + byMinute;
        for (let minute = initialMinutes; minute < maxMinutes; minute += byMinuteWithHours) {
            minutes.push(minute);
        }
        hours.push(0);
    }

    return { hours, minutes };
};

// TRANSLATE FUNCTIONS
const getRRuleDescription = (values, translateOptions = { translate: () => {}, language: 'es' }) => {
    const { start, startDuration, repeat, frequency, selectedWeekDays, untilType, untilCount, untilDate, byHour, byMinute } = values;

    if (!repeat) {
        return '';
    }

    const dateText = getDateDescription(start, startDuration, translateOptions);
    const descriptionText = translateOptions?.translate('description');
    const intradayText = getIntradayDescription(byHour, byMinute, translateOptions.translate);
    const frequencyText = getFrequencyDescription(frequency, selectedWeekDays, translateOptions.translate);
    const untilText = getUntilDescription(untilType, untilCount, untilDate, translateOptions);

    const parts = [dateText, descriptionText, intradayText, frequencyText, untilText].filter(Boolean);
    return parts.join(' ');
};

const getDateDescription = (start, startDuration, translateOptions = { translate: () => {}, language: 'es' }) => {
    if (start?.isValid && startDuration?.isValid) {
        const startDate = start.set({
            hour: startDuration.hour,
            minute: startDuration.minute,
            second: 0,
            millisecond: 0,
        });

        const jsDate = DateTimeToJSDate(startDate);

        return translateOptions?.translate('date_description', { date: formatDateLong(jsDate, translateOptions?.language || 'es') });
    }

    return '';
};

const getIntradayDescription = (byHour = 0, byMinute = 0, translate) => {
    const parts = [];

    // TODO: MEJORAR CON I18N
    if (byHour > 0) {
        const byHourDescription = byHour > 1 ? translate('intraday_description.hours', { byHour }) : translate('intraday_description.hour', { byHour });
        parts.push(byHourDescription);
    }

    // TODO: MEJORAR CON I18N
    if (byMinute > 0) {
        const byMinuteDescription = byMinute > 1 ? translate('intraday_description.minutes', { byMinute }) : translate('intraday_description.minute', { byMinute });
        parts.push(byMinuteDescription);
    }

    if (parts?.length <= 0) {
        return '';
    }

    return translate('intraday_description.intraday', { result: parts.join(translate('and')) });
};

const getFrequencyDescription = (frequency, selectedWeekDays = [], translate) => {
    if (frequency === FREQUENCY.CUSTOM) {
        return getWeekDaysDescription(selectedWeekDays, translate);
    }

    switch (frequency) {
        case FREQUENCY.DAILY:
            return translate('frequency_description.daily');
        case FREQUENCY.WEEKLY:
            return translate('frequency_description.weekly');
        case FREQUENCY.WEEKENDS:
            return translate('frequency_description.weekends');
        case FREQUENCY.BUSINESS_DAYS:
            return translate('frequency_description.business_days');
        default:
            return '';
    }
};

const getWeekDaysDescription = (selectedWeekDays = [], translate) => {
    if (!Array.isArray(selectedWeekDays) || selectedWeekDays?.length <= 0) {
        return '';
    }

    const days = selectedWeekDays.map((day) => translate(`week_days.${day}`))?.filter(Boolean);

    if (days.length === 1) {
        return days[0];
    }

    if (days.length === 2) {
        return `${days[0]} ${translate('and')} ${days[1]}`;
    }

    const lastDay = days.pop() || '';

    return translate('week_days_description', { days: days.join(', '), lastDay });
};

// TODO: MEJORAR CON I18N
const getUntilDescription = (untilType, untilCount, untilDate, translateOptions = { translate: () => {}, language: 'es' }) => {
    if (untilType === UNTIL_TYPE.COUNT && untilCount > 0) {
        return translateOptions?.translate('until_count_description', { untilCount });
    }

    if (untilType === UNTIL_TYPE.DATE && untilDate?.isValid) {
        return translateOptions?.translate('until_date_description', { date: formatDateLong(untilDate?.toJSDate(), translateOptions?.language || 'es') });
    }

    return '';
};

const DateTimeToJSDate = (date) => {
    const jsDate = new Date(date.year, date.month - 1, date.day, date.hour, date.minute, date.second, date.millisecond);

    return jsDate;
};
